import type { User } from "@supabase/supabase-js";

import { AuthMachineStatus } from "../../constants";
import { useAuth } from "../../contexts/AuthContext";
import { useAuthMachineState } from "../../store/authMachine";

export interface UseAuthV2Result {
  user: User | null;
  authStatus: AuthMachineStatus;
  isAuthenticated: boolean;
  isPasswordResetPending: boolean;
  isFreshSignIn: boolean;
  signIn: ReturnType<typeof useAuth>["signIn"];
  signOut: ReturnType<typeof useAuth>["signOut"];
  updatePassword: ReturnType<typeof useAuth>["updatePassword"];
  resetPasswordForEmail: ReturnType<typeof useAuth>["resetPasswordForEmail"];
  markSignInComplete: ReturnType<typeof useAuth>["markSignInComplete"];
}

/**
 * Successor API for auth members, improved incrementally. Reads the auth machine
 * state directly instead of going through `useAuth()`'s machine-derived fields, and
 * delegates operations to `useAuth()` (one behavior path — no wrapping).
 *
 * `useAuth()`'s auth members (`user`, `isFreshSignIn`, `signIn`, `signOut`,
 * `updatePassword`, `resetPasswordForEmail`, `markSignInComplete`) are legacy;
 * prefer this hook for them. `useAuth()`'s non-auth members (`firmContext`,
 * `hasPermission`, …) are unaffected and keep using `useAuth()` directly until
 * they migrate separately.
 */
export const useAuthV2 = (): UseAuthV2Result => {
  const state = useAuthMachineState();
  const { signIn, signOut, updatePassword, resetPasswordForEmail, markSignInComplete } = useAuth();
  return {
    user: state.user,
    authStatus: state.status,
    isAuthenticated:
      state.status === AuthMachineStatus.AUTHENTICATED ||
      state.status === AuthMachineStatus.PASSWORD_RESET_PENDING,
    isPasswordResetPending: state.status === AuthMachineStatus.PASSWORD_RESET_PENDING,
    isFreshSignIn: state.isFreshSignIn,
    signIn,
    signOut,
    updatePassword,
    resetPasswordForEmail,
    markSignInComplete,
  };
};
